/**
 * SalesIQ Chat Service — singleton that initializes once after login.
 * Manages conversations, messages, history, file uploads.
 * No Zoho JS SDK, no iframe — 100% native REST API.
 */
import { reactive, toRefs } from 'vue'
import axiosLib from 'axios'
import { CONFIG } from '@/const/config'
import { getAccessToken } from '@/helpers/utils'

import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'

export interface ChatMessage {
  id: string
  from: 'user' | 'agent'
  text: string
  time: Date
  agentName?: string
  reactions?: string[]
  isFile?: boolean
  fileName?: string
  fileSize?: string
  fileUrl?: string
}

export interface ConversationTag { name: string; color: string }

export interface HistoryItem {
  id: string
  agentName: string
  preview: string
  time: string
  status: string
  isOpen: boolean
  tags: ConversationTag[]
}

// ── Singleton state ──
let initialized = false
let msgId = 0
let hubConnection: any = null
let pollTimer: ReturnType<typeof setInterval> | null = null
let heartbeatTimer: ReturnType<typeof setInterval> | null = null
const seenIds = new Set<string>()
let savedActiveMessages: ChatMessage[] = []

function getChatBaseURL(): string {
  try {
    const mode = localStorage.getItem('dev_api_mode')
    if (mode === 'remote') return 'https://business.dev.nb2.tech'
    if (mode === 'local' || mode === 'mock') return ''
  } catch { /* SSR or no localStorage */ }
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') return ''
  return CONFIG.VUE_APP_API_URL
}

const chatApi = axiosLib.create({
  baseURL: getChatBaseURL(),
  headers: { 'x-api-key': CONFIG.VUE_APP_X_API_KEY },
})

chatApi.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

const savedConvId = localStorage.getItem('siq_conversation_id') || ''
const savedVisitorId = localStorage.getItem('siq_visitor_id') || ''

const state = reactive({
  messages: [] as ChatMessage[],
  isAgentTyping: false,
  agentName: '',
  chatStatus: savedConvId ? 'active' : 'idle' as 'idle' | 'waiting' | 'active' | 'ended',
  conversationId: savedConvId,
  visitorId: savedVisitorId,
  unreadCount: 0,
  isViewingHistory: false,
  viewingHistoryId: '',
  // History & metadata
  historyList: [] as HistoryItem[],
  isLoading: true,
  activeConvMeta: { tags: [] as ConversationTag[], preview: '', ownerName: '' },
})

function genId(): string {
  return `msg_${Date.now()}_${++msgId}`
}

// ── SignalR ──
async function connectSignalR() {
  if (hubConnection) return
  try {
    const token = getAccessToken()
    hubConnection = new HubConnectionBuilder()
      .withUrl('/chatHub', { accessTokenFactory: () => getAccessToken() })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build()

    hubConnection.on('ReceiveMessage', (msg: any) => {
      if (msg.sender === 'agent' && msg.text) {
        if (state.isViewingHistory) { state.unreadCount++; return }
        state.messages.push({
          id: genId(), from: 'agent', text: msg.text,
          time: new Date(msg.timestamp || Date.now()),
          agentName: msg.agentName || 'Support',
        })
        state.unreadCount++
        state.isAgentTyping = false
        state.chatStatus = 'active'
      }
    })

    hubConnection.on('ChatEnded', () => {
      state.chatStatus = 'ended'
      state.isAgentTyping = false
      stopHeartbeat()
    })

    await hubConnection.start()
  } catch {
    hubConnection = null
  }
}

// ── Session restore ──
async function restoreSession() {
  if (!state.conversationId) return
  try {
    await chatApi.post('/api/chat/restore', {
      conversationId: state.conversationId,
      visitorId: state.visitorId,
    })
  } catch { /* ignore */ }
}

// ── Message parsing (shared between load + history view) ──
function parseMessages(msgs: any[]): ChatMessage[] {
  const result: ChatMessage[] = []
  for (const msg of msgs) {
    if (msg.type !== 'text' && msg.type !== 'file') continue

    if (msg.type === 'file') {
      const file = msg.message?.file
      if (!file) continue
      result.push({
        id: msg.id, from: msg.sender?.type === 'operator' ? 'agent' : 'user',
        text: file.name || 'File', time: new Date(Number(msg.time)),
        agentName: msg.sender?.type === 'operator' ? (msg.sender?.name || 'Support') : undefined,
        isFile: true, fileName: file.name, fileSize: file.size, fileUrl: file.url,
      })
    } else {
      const text = msg.message?.text
      if (!text) continue
      result.push({
        id: msg.id, from: msg.sender?.type === 'operator' ? 'agent' : 'user',
        text, time: new Date(Number(msg.time)),
        agentName: msg.sender?.type === 'operator' ? (msg.sender?.name || 'Support') : undefined,
      })
    }
  }
  return result
}

// ── Load conversation messages ──
async function loadConversationMessages(convId: string): Promise<ChatMessage[]> {
  try {
    const { data } = await chatApi.get(`/api/chat/history/${convId}/messages`)
    const raw = typeof data === 'string' ? JSON.parse(data) : data
    const msgs = raw?.data
    if (!Array.isArray(msgs)) return []
    return parseMessages(msgs)
  } catch { return [] }
}

// ── Load full active conversation on init ──
async function loadActiveConversation() {
  if (!state.conversationId) return

  // Try stored ID first
  let msgs = await loadConversationMessages(state.conversationId)

  // Fallback: find open conversation from history
  if (msgs.length === 0) {
    try {
      const vp = state.visitorId ? `?visitorId=${state.visitorId}` : ''
      const { data: histData } = await chatApi.get(`/api/chat/history${vp}`)
      const convs = typeof histData === 'string' ? JSON.parse(histData) : histData
      const items = convs?.data
      if (Array.isArray(items)) {
        const openConv = items.find((c: any) => c.type === 'chat' && c.chat_status?.status_key === 'open')
        if (openConv) {
          if (openConv.id !== state.conversationId) {
            state.conversationId = openConv.id
            localStorage.setItem('siq_conversation_id', openConv.id)
          }
          msgs = await loadConversationMessages(openConv.id)
        }
      }
    } catch { /* ignore */ }
  }

  // Replace messages (don't append — prevents duplicates on re-init)
  state.messages.splice(0, state.messages.length, ...msgs)
  seenIds.clear()
  for (const m of msgs) seenIds.add(m.id)

  // Set agent name from messages
  if (!state.agentName) {
    const agentMsg = msgs.find(m => m.from === 'agent' && m.agentName)
    if (agentMsg) state.agentName = agentMsg.agentName!
  }
}

// ── Load history list + active conversation metadata ──
async function loadHistoryList() {
  try {
    const visitorParam = state.visitorId ? `?visitorId=${state.visitorId}` : ''
    const { data } = await chatApi.get(`/api/chat/history${visitorParam}`)
    const convs = typeof data === 'string' ? JSON.parse(data) : data
    const items = convs?.data
    if (!Array.isArray(items)) return

    // Check if active conversation was closed externally
    const activeConv = items.find((c: any) => c.type === 'chat' && c.chat_status?.status_key === 'open')
    if (state.conversationId && !activeConv && !state.isViewingHistory) {
      // Our active conversation was closed (admin ended it or idle timeout)
      state.chatStatus = 'ended'
      state.conversationId = ''
      state.visitorId = ''
      localStorage.removeItem('siq_conversation_id')
      localStorage.removeItem('siq_visitor_id')
      stopPolling()
      stopHeartbeat()
    }

    // Extract active conversation metadata
    if (activeConv) {
      state.activeConvMeta = {
        tags: Array.isArray(activeConv.tags) ? activeConv.tags.map((t: any) => ({ name: t.name, color: t.color })) : [],
        preview: activeConv.last_message_info?.message?.text || activeConv.question || '',
        ownerName: activeConv.attender?.name || activeConv.owner?.name || '',
      }

      // Auto-restore if localStorage was cleared
      if (!state.conversationId) {
        state.conversationId = activeConv.id
        state.chatStatus = 'active'
        localStorage.setItem('siq_conversation_id', activeConv.id)
        if (state.activeConvMeta.ownerName) state.agentName = state.activeConvMeta.ownerName
        chatApi.post('/api/chat/restore', { conversationId: activeConv.id, visitorId: '' }).catch(() => {})
        const msgs = await loadConversationMessages(activeConv.id)
        for (const m of msgs) seenIds.add(m.id)
        state.messages.splice(0, state.messages.length, ...msgs)
        startPolling()
        startHeartbeat()
      }
    }

    // Build history list (exclude open conversations)
    state.historyList = items
      .filter((c: any) => c.type === 'chat' && c.chat_status?.status_key !== 'open')
      .map((c: any) => {
        const d = new Date(Number(c.start_time))
        return {
          id: c.id,
          agentName: c.attender?.name || c.owner?.name || 'Support',
          preview: c.last_message_info?.message?.text || c.question || '',
          time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
          status: c.chat_status?.status_key === 'open' ? 'Active' : 'Closed',
          isOpen: c.chat_status?.status_key === 'open',
          tags: Array.isArray(c.tags) ? c.tags.map((t: any) => ({ name: t.name, color: t.color })) : [],
        }
      })
  } catch { /* silently fail */ } finally {
    state.isLoading = false
  }
}

// ── Polling ──
let pollFailCount = 0

async function pollMessages() {
  if (!state.conversationId || state.chatStatus === 'ended') return
  if (state.isViewingHistory) return

  let msgs: any[] | null = null

  // Try the fast poll endpoint first
  try {
    const { data } = await chatApi.get('/api/chat/messages')
    const raw = data?.data
    if (Array.isArray(raw)) {
      msgs = raw
      pollFailCount = 0
    }
  } catch { /* fast poll failed */ }

  // Fallback: use history messages endpoint (more reliable, not rate-limited as aggressively)
  if (!msgs) {
    pollFailCount++
    // Only fallback every 3rd failure to avoid hammering both endpoints
    if (pollFailCount % 3 === 0) {
      try {
        const loaded = await loadConversationMessages(state.conversationId)
        // Find messages we don't have yet
        for (const m of loaded) {
          if (seenIds.has(m.id)) continue
          // Check text-based dedup for user messages (optimistic sends use different IDs)
          if (m.from === 'user') {
            const alreadyHave = state.messages.some(
              ex => ex.from === 'user' && ex.text === m.text &&
                Math.abs(new Date(ex.time).getTime() - new Date(m.time).getTime()) < 30000
            )
            if (alreadyHave) { seenIds.add(m.id); continue }
          }
          seenIds.add(m.id)
          state.messages.push(m)
          if (m.from === 'agent') {
            if (!window.location.pathname.includes('/chat')) state.unreadCount++
            state.isAgentTyping = false
            state.chatStatus = 'active'
            if (!state.agentName && m.agentName) state.agentName = m.agentName
          }
        }
      } catch { /* ignore */ }
    }
    return
  }

  // Process fast-poll results
  for (const msg of msgs) {
    if (msg.type !== 'text') continue
    if (seenIds.has(msg.id)) continue
    seenIds.add(msg.id)

    const rawText = msg.message?.text
    if (!rawText) continue
    const senderType = msg.sender?.type

    if (senderType === 'operator' || senderType === 'bot') {
      const alreadyHave = state.messages.some(
        m => m.from === 'agent' && m.text === rawText &&
          Math.abs(new Date(m.time).getTime() - Number(msg.time)) < 10000
      )
      if (!alreadyHave) {
        state.messages.push({
          id: msg.id,
          from: 'agent',
          text: rawText,
          time: new Date(Number(msg.time)),
          agentName: msg.sender?.name || 'Support',
        })
        if (!window.location.pathname.includes('/chat')) state.unreadCount++
        state.isAgentTyping = false
        state.chatStatus = 'active'
        if (!state.agentName && msg.sender?.name) state.agentName = msg.sender.name
      }
    } else if (senderType === 'visitor') {
      // Dedup user messages — optimistic sends use genId(), server has real ID
      const alreadyHave = state.messages.some(
        m => m.from === 'user' && m.text === rawText &&
          Math.abs(new Date(m.time).getTime() - Number(msg.time)) < 30000
      )
      if (!alreadyHave) {
        state.messages.push({
          id: msg.id, from: 'user', text: rawText,
          time: new Date(Number(msg.time)),
        })
      }
    }
  }
}

let historyRefreshCount = 0

function startPolling() {
  if (pollTimer) return
  pollMessages()
  pollTimer = setInterval(() => {
    pollMessages()
    // Refresh history every 3rd poll (30s) to detect closed conversations
    if (++historyRefreshCount >= 3) {
      historyRefreshCount = 0
      loadHistoryList()
    }
  }, 10000)
}

function stopPolling() {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
}

function startHeartbeat() {
  if (heartbeatTimer) return
  heartbeatTimer = setInterval(async () => {
    if (!state.conversationId || state.chatStatus !== 'active') return
    try { await chatApi.post('/api/chat/heartbeat') } catch { /* ignore */ }
  }, 30000)
}

function stopHeartbeat() {
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null }
}

// ── Public: Initialize (called once from layout after login) ──
export function initSalesIQChat() {
  if (initialized) {
    if (state.conversationId && !pollTimer) startPolling()
    return
  }
  initialized = true
  connectSignalR()

  if (state.conversationId) {
    restoreSession().then(() => {
      loadActiveConversation()
      startPolling()
    })
    startHeartbeat()
  }

  // Load history in background
  loadHistoryList()
}

// ── Public: Composable (returns reactive state + actions) ──
export function useSalesIQChat() {
  async function sendMessage(text: string) {
    if (!text.trim()) return
    const trimmed = text.trim()

    if (state.isViewingHistory) returnToActiveChat()

    state.unreadCount = 0

    try {
      if (!state.conversationId) {
        // New conversation — clear old messages and seenIds
        state.messages.splice(0)
        seenIds.clear()
        state.messages.push({ id: genId(), from: 'user', text: trimmed, time: new Date() })
        state.chatStatus = 'waiting'
        const { data } = await chatApi.post('/api/chat/start', { message: trimmed })
        state.conversationId = data.conversationId
        state.visitorId = data.visitorId || ''
        state.chatStatus = 'active'
        localStorage.setItem('siq_conversation_id', data.conversationId)
        localStorage.setItem('siq_visitor_id', data.visitorId || '')
        startPolling()
        startHeartbeat()
        loadHistoryList() // Refresh to move old closed conv to history
      } else {
        state.messages.push({ id: genId(), from: 'user', text: trimmed, time: new Date() })
        await chatApi.post('/api/chat/messages', { text: trimmed })
      }
    } catch (err: any) {
      console.error('[Chat] Send failed:', err?.response?.data?.error || err.message)
      state.chatStatus = state.conversationId ? 'active' : 'idle'
    }
  }

  async function viewHistory(historyId: string) {
    savedActiveMessages = [...state.messages]
    state.isViewingHistory = true
    state.viewingHistoryId = historyId
    state.messages.splice(0, state.messages.length)
    const msgs = await loadConversationMessages(historyId)
    state.messages.splice(0, state.messages.length, ...msgs)
  }

  function returnToActiveChat() {
    state.isViewingHistory = false
    state.viewingHistoryId = ''
    state.messages.splice(0, state.messages.length, ...savedActiveMessages)
    savedActiveMessages = []
  }

  function endChat() {
    state.chatStatus = 'ended'
    state.isAgentTyping = false
    state.conversationId = ''
    state.visitorId = ''
    state.isViewingHistory = false
    state.viewingHistoryId = ''
    localStorage.removeItem('siq_conversation_id')
    localStorage.removeItem('siq_visitor_id')
    stopPolling()
    stopHeartbeat()
  }

  async function uploadFile(file: File): Promise<boolean> {
    if (!state.conversationId) return false

    state.messages.push({
      id: genId(), from: 'user', text: file.name, time: new Date(),
      isFile: true, fileName: file.name, fileSize: String(file.size),
    })

    try {
      const formData = new FormData()
      formData.append('file', file)
      await chatApi.post('/api/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return true
    } catch (err: any) {
      console.error('[Chat] Upload failed:', err?.response?.data?.error || err.message)
      return false
    }
  }

  function clearUnread() { state.unreadCount = 0 }

  function refreshHistory() { loadHistoryList() }

  return {
    ...toRefs(state),
    sendMessage,
    uploadFile,
    endChat,
    clearUnread,
    viewHistory,
    returnToActiveChat,
    refreshHistory,
  }
}
